'use client';

import { useCallback, useEffect, useMemo, useRef, type RefObject } from 'react';
import type { SquishLayout } from '@usespaceui/squishmoji';
import { voiceAvatarGaze } from './voice-avatar-gaze';
import { constrainAvatarGazeOffset } from './avatar-gaze-containment';
import { dampAvatarGaze } from './avatar-gaze-damping';

/** Event-driven eye movement, shared by the web and Electron web renderer. */
export function useVoiceAvatarGaze({
  svgRef,
  gazeRef,
  enabled,
  layoutRef,
  identity,
}: {
  svgRef: RefObject<SVGSVGElement | null>;
  gazeRef: RefObject<SVGGElement | null>;
  enabled: boolean;
  layoutRef?: RefObject<SquishLayout>;
  identity?: string;
}) {
  const hasPointer = useRef(false);
  const refreshRef = useRef<(() => void) | null>(null);
  const refresh = useCallback(() => refreshRef.current?.(), []);
  useEffect(() => {
    const svg = svgRef.current;
    const group = gazeRef.current;
    if (!svg || !group || !enabled) return;
    let frame: number | null = null;
    let pointer: { x: number; y: number } | null = null;
    let returning = false;
    let current = { x: 0, y: 0 };
    let previousTime: number | null = null;
    let previousLayout = layoutRef?.current;
    const write = () =>
      group.setAttribute(
        'transform',
        `translate(${Math.round(current.x * 1000) / 1000} ${Math.round(current.y * 1000) / 1000})`
      );
    const reset = () => {
      if (frame !== null) cancelAnimationFrame(frame);
      frame = null;
      pointer = null;
      returning = false;
      hasPointer.current = false;
      current = { x: 0, y: 0 };
      previousTime = null;
      write();
    };
    const draw = (now: number) => {
      frame = null;
      if ((!pointer && !returning) || document.visibilityState !== 'visible')
        return;
      const target = pointer
        ? voiceAvatarGaze(
            pointer,
            svg.getBoundingClientRect(),
            layoutRef?.current
          )
        : { x: 0, y: 0 };
      const next = dampAvatarGaze(
        current,
        target,
        previousTime === null ? 16 : now - previousTime
      );
      previousTime = now;
      current = layoutRef
        ? constrainAvatarGazeOffset(next.point, layoutRef.current)
        : next.point;
      previousLayout = layoutRef?.current;
      write();
      if (!next.settled) schedule();
      else {
        previousTime = null;
        returning = false;
      }
    };
    const schedule = () => {
      if ((pointer || returning) && frame === null)
        frame = requestAnimationFrame(draw);
    };
    const returnToRest = () => {
      if (!hasPointer.current) return;
      pointer = null;
      returning = true;
      // Keep automatic drift suppressed after mouse interaction, including
      // the return to rest, so it cannot pop back in at the final frame.
      schedule();
    };
    refreshRef.current = () => {
      const layout = layoutRef?.current;
      if ((!pointer && !returning) || !layout) return;
      const unchanged =
        previousLayout?.body === layout.body &&
        previousLayout.eyes.length === layout.eyes.length &&
        layout.eyes.every((eye, index) => {
          const previous = previousLayout?.eyes[index];
          return (
            previous &&
            previous.width === eye.width &&
            previous.height === eye.height &&
            previous.matrix.every((value, part) => value === eye.matrix[part])
          );
        });
      if (unchanged) return;
      previousLayout = layout;
      current = constrainAvatarGazeOffset(current, layout);
      write();
      schedule();
    };
    const move = (event: PointerEvent) => {
      if (
        event.pointerType !== 'mouse' ||
        document.visibilityState !== 'visible'
      ) {
        reset();
        return;
      }
      pointer = { x: event.clientX, y: event.clientY };
      returning = false;
      hasPointer.current = true;
      schedule();
    };
    const leave = (event: PointerEvent) => {
      if (event.relatedTarget === null) returnToRest();
    };
    const press = (event: PointerEvent) => {
      if (event.pointerType !== 'mouse') reset();
    };
    const visibility = () => {
      if (document.visibilityState !== 'visible') reset();
    };
    window.addEventListener('pointermove', move, {
      passive: true,
      capture: true,
    });
    window.addEventListener('pointerdown', press, { passive: true });
    window.addEventListener('pointerout', leave);
    window.addEventListener('blur', returnToRest);
    window.addEventListener('resize', schedule);
    document.addEventListener('scroll', schedule, {
      passive: true,
      capture: true,
    });
    document.addEventListener('visibilitychange', visibility);
    return () => {
      reset();
      refreshRef.current = null;
      window.removeEventListener('pointermove', move, true);
      window.removeEventListener('pointerdown', press);
      window.removeEventListener('pointerout', leave);
      window.removeEventListener('blur', returnToRest);
      window.removeEventListener('resize', schedule);
      document.removeEventListener('scroll', schedule, true);
      document.removeEventListener('visibilitychange', visibility);
    };
  }, [svgRef, gazeRef, enabled, layoutRef, identity]);
  return useMemo(() => ({ hasPointer, refresh }), [refresh]);
}
